import { POSTAL_CODE_REGEX } from '@/constants/regex';
import { useGetSellerAddress } from '@/hooks/sellers/useGetSellerAddress';
import { sellerAddressResponseToSellerAddressUi } from '@/hooks/sellers/useGetSellerAddress/mapper';
import { useUpdateSellerAddress } from '@/hooks/sellers/useUpdateSellerAddress';
import { WritableDraft } from 'immer';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { initialState, SellerAddressUi } from './SellerAddressUi';

export function useSellerAddressViewController() {
  const [uiState, setUiState] = useImmer<SellerAddressUi>(initialState);
  const sellerId = useParams().sellerId;
  const savedDataUiRef = useRef<SellerAddressUi>();
  const sellerAddressHook = useGetSellerAddress(sellerId);
  const updateSellerAddressHook = useUpdateSellerAddress();

  useEffect(() => {
    if (!sellerAddressHook.data) return;
    savedDataUiRef.current = sellerAddressResponseToSellerAddressUi(
      sellerAddressHook.data
    );
    setUiState(savedDataUiRef.current);
  }, [sellerAddressHook.data]);

  const onChangedStreet = (newStreet: string) =>
    setUiState((draft) => {
      draft.street = newStreet;
      draft.streetError = !newStreet.trim() ? 'Calle inválido' : undefined;
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const onChangedExteriorNumber = (newExteriorNumber: string) =>
    setUiState((draft) => {
      draft.exteriorNumber = newExteriorNumber;
      draft.exteriorNumberError = !newExteriorNumber.trim()
        ? 'Número exterior inválido'
        : undefined;
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const onChangedInteriorNumber = (newInteriorNumber: string) =>
    setUiState((draft) => {
      draft.interiorNumber = newInteriorNumber;
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const onChangedPostalCode = (newPostalCode: string) =>
    setUiState((draft) => {
      draft.postalCode = newPostalCode;
      draft.postalCodeError = !POSTAL_CODE_REGEX.test(newPostalCode)
        ? 'Código postal inválido'
        : undefined;
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const onChangedColonia = (newColonia: string) =>
    setUiState((draft) => {
      draft.colonia = newColonia;
      draft.coloniaError = !newColonia.trim() ? 'Colonia inválida' : undefined;
      draft.cancelDisabled = false;
      calculateSaveButtonState(draft);
    });

  const calculateSaveButtonState = (draft: WritableDraft<SellerAddressUi>) => {
    const isValidPostalCode = POSTAL_CODE_REGEX.test(draft.postalCode ?? '');
    draft.saveDisabled =
      !isValidPostalCode ||
      !draft.street?.trim() ||
      !draft.exteriorNumber?.trim();
  };

  const onClickedSave = () => {
    if (!sellerId) return;
    updateSellerAddressHook.mutate({
      sellerId,
      data: uiState,
    });
  };

  const onClickedCancel = () =>
    savedDataUiRef.current && setUiState(savedDataUiRef.current);

  return {
    uiState,
    actions: {
      onChangedStreet,
      onChangedExteriorNumber,
      onChangedInteriorNumber,
      onChangedPostalCode,
      onChangedColonia,
      onClickedCancel,
      onClickedSave,
    },
  };
}
